import esprima from 'esprima';

const createResult = (passed, { feedback = '', node }) => {
  if (passed) {
    return {
      passed,
      feedback: feedback || '',
    };
  }
  let f = feedback;
  if (node) f = `Line ${node.loc.start.line}: ${f}`
  return {
    passed,
    feedback: f,
  };
};

const tokensMatch = (a, b) =>
  a.type === b.type && a.value === b.value;

const listContainsToken = (list, token) => {
  const len = list.length;

  for (let i = 0; i < len; i++) {
    const lToken = list[i];
    if (tokensMatch(lToken, token)) {
      return i;
    }
  }

  return -1;
}

const removeAtIndex = (arr, index) =>
  [...arr.slice(0, index), ...arr.slice(index + 1)];

const testAll = (...x) =>
  x.reduce((result, j) => j.passed ? result : j, x[0]);

// compareTrees recursively compares two ASTs
const compareTrees = (a, b) => {
  if (typeof a === 'undefined'
    || (Array.isArray(a) && typeof a[0] === 'undefined')
    || (a.type === 'Identifier' && a.name === '_')
    ||
      (a.type === 'ExpressionStatement'
      && a.expression.type === 'Identifier'
      && a.expression.name === '_'
    )
  ) {
      return createResult(true, {
        feedback: 'The Candidate matches the expected structure.',
      });
  }

  if (typeof b === 'undefined'
    || a.type !== b.type
  )  {
    return createResult(false, {
      node: a,
      feedback: `Expected ${a.type}. Received ${b && b.type}.`,
    });
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    return testAll(
      ...a.map((t, index) => compareTrees(t, b[index])));
  }

  switch (a.type) {
    case 'IfStatement': {
      return testAll(
        compareTrees(a.test, b.test),
        compareTrees(a.consequent, b.consequent),
      );
    }
    case 'BlockStatement':
    case 'Program': {
      return compareTrees(a.body, b.body);
    }
    case 'ExpressionStatement': {
      return compareTrees(a.expression, b.expression);
    }
    case 'FunctionExpression':
    case 'ArrowFunctionExpression': {
      return testAll(
        compareTrees(a.body, b.body),
        compareTrees(a.params, b.params),
      );
    }
    case 'ForStatement': {
      return testAll(
        compareTrees(a.body, b.body),
        compareTrees(a.test, b.test),
        compareTrees(a.init, b.init),
        compareTrees(a.update, b.update),
      );
    }
    case 'WhileStatement': {
      return testAll(
        compareTrees(a.body, b.body),
        compareTrees(a.test, b.test),
      );
    }
    case 'BinaryExpression': {
      const operatorComparison = (a.operator === b.operator) ? (
        createResult(true, {
          feedback: 'The Candidate matches the expected structure.',
        })
      ) : (
        createResult(false, {
          node: a,
          feedback: `Expected BinaryExpression: ${a.operator}. Received BinaryExpression: ${b && b.operator}.`,
        })
      );
      return testAll(
        operatorComparison,
        compareTrees(a.left, b.left),
        compareTrees(a.right, b.right),
      )
    }
    case 'Literal': {
      if (a.raw === b.raw) {
        return createResult(true, {
          feedback: 'The Candidate matches the expected structure.',
        });
      }
      return createResult(false, {
        node: a,
        feedback: `Expected Literal: ${a.value}. Received Literal: ${b && b.value}.`,
      });
    }
    case 'VariableDeclaration': {
      return compareTrees(a.declarations, b.declarations);
    }
    case 'VariableDeclarator': {
      return testAll(
        compareTrees(a.id, b.id),
        compareTrees(a.init, b.init),
      );
    }
    case 'ObjectExpression': {
      return compareTrees(a.properties, b.properties);
    }
    case 'Property': {
      return testAll(
        compareTrees(a.key, b.key),
        compareTrees(a.value, b.value),
      );
    }
    case 'ArrayExpression': {
      return compareTrees(a.elements, b.elements);
    }
    case 'ReturnStatement': {
      return compareTrees(a.argument, b.argument);
    }
    case 'ThisExpression': {
      return createResult(true, {
        feedback: 'The Candidate matches the expected structure.',
      });
    }
    case 'UnaryExpression':
    case 'UpdateExpression': {
      if (a.operator !== b.operator) {
        return createResult(false, {
          node: a,
          feedback: `Expected Identifier: ${a.operator}. Received Identifier: ${b && b.operator}.`,
        });
      }
      return compareTrees(a.argument, b.argument);
    }
    case 'LogicalExpression': {
      if (a.operator !== b.operator) {
        return createResult(false, {
          node: a,
          feedback: `Expected Identifier: ${a.operator}. Received Identifier: ${b && b.operator}.`,
        });
      }
      return testAll(
        compareTrees(a.left, b.left),
        compareTrees(a.right, b.right),
      );
    }
    case 'Identifier': {
      if (a.name === b.name) {
        return createResult(true, {
          feedback: 'The Candidate matches the expected structure.',
        });
      }
      return createResult(false, {
        node: a,
        feedback: `Expected Identifier: ${a.name}. Received Identifier: ${b && b.name}.`,
      });
    }
    default:
      console.log(a);
      return createResult(false, {
        node: a,
        feedback: `You used an unsupported feature: ${a.type}.`,
      });
  }
}

// match compares the structure of structure with candidate and
// asserts that the structures are the same.
export const match = (structure, candidate) => {
  try {
    const structureAST = esprima.parse(structure, { loc: true });
    const candidateAST = esprima.parse(candidate);
    return compareTrees(structureAST, candidateAST);
  } catch (e) {
    if (e.description) {
      return createResult(false, {
        feedback: `${e.description} | Line: ${e.lineNumber} | Column: ${e.column}`,
      });
    } else {
      throw e;
    }
  }
}

// contain asserts that the candidate contains all the tokens
// in the structure.
export const contain = (structure, candidate) => {
  const structureTokens = esprima.tokenize(structure, { loc: true });
  let candidateTokens = esprima.tokenize(candidate);

  const len = structureTokens.length;
  for (let i = 0; i < len; i++) {
    const token = structureTokens[i];

    const matchIndex = listContainsToken(candidateTokens, token);
    if (matchIndex < 0) {
      return createResult(false, {
        feedback: `Expected to find: ${token.type} '${token.value}'`,
      });
    } else {
      candidateTokens = removeAtIndex(candidateTokens, matchIndex);
    }
  }

  return createResult(true, {
    feedback: 'The candidate contains all of the expected tokens',
  });
}

// does not contain asserts that none of the tokens in structure
// are found in candidate
export const doesNotContain = (structure, candidate) => {
  const structureTokens = esprima.tokenize(structure);
  let candidateTokens = esprima.tokenize(candidate, { loc: true });

  const len = structureTokens.length;
  for (let i = 0; i < len; i++) {
    const token = structureTokens[i];

    const matchIndex = listContainsToken(candidateTokens, token);
    if (matchIndex > -1) {
      return createResult(false, {
        node: candidateTokens[matchIndex],
        feedback: `The candidate contains unexpected: ${token.type} '${token.value}'`,
      });
    }
  }

  return createResult(true, {
    feedback: 'The candidate does not contains any of the expected tokens',
  });
}

export default {
  match,
  contain,
  doesNotContain,
};



// WEBPACK FOOTER //
// ./src/codeCheck/index.js